// The shift pill — shared by the read-only team grid, agenda lists, and the
// builder. Color is never the sole signal: the position name always renders.
import type { Shift } from '../lib/types'
import { formatTimeRange } from '../lib/format'
import { Avatar } from './ui'

export function ShiftPill({
  shift,
  positionColor,
  onClick,
  showPosition = false,
  highlight = false,
  conflict = false,
  pendingDot = false,
}: {
  shift: Shift
  positionColor: string
  onClick?: () => void
  showPosition?: boolean
  highlight?: boolean
  conflict?: boolean
  pendingDot?: boolean
}) {
  const isOpen = shift.status === 'open'
  const isDraft = !shift.published
  const base = isOpen
    ? 'bg-sunbeam/25 border-sunbeam'
    : isDraft
      ? 'border-dashed border-stone/50 bg-sand/40'
      : 'bg-white border-hairline'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`relative flex w-full min-h-[44px] items-stretch gap-2 rounded-md border text-left shadow-card transition-shadow ${base} ${
        highlight ? 'ring-2 ring-lake' : ''
      } ${onClick ? 'hover:shadow-md' : 'cursor-default'}`}
    >
      <span className="w-[3px] shrink-0 rounded-l-md" style={{ backgroundColor: positionColor }} aria-hidden />
      <span className="flex min-w-0 flex-1 flex-col justify-center py-1.5 pr-2">
        <span className="tnum flex items-center gap-1.5 text-sm font-semibold">
          {formatTimeRange(shift.startTime, shift.endTime, shift.endsNextDay)}
          <span className="tnum text-xs font-normal text-stone">{shift.durationHours}h</span>
          {conflict && (
            <span className="text-danger" title="Conflict" aria-label="Conflict">
              ⚠
            </span>
          )}
          {pendingDot && <span className="h-2 w-2 rounded-full bg-sunset" title="Pending request" />}
        </span>
        {isOpen ? (
          <span className="text-xs font-bold uppercase tracking-wider text-clay">
            Open{shift.claimCount > 0 && ` · ${shift.claimCount} request${shift.claimCount > 1 ? 's' : ''}`}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 truncate text-sm text-ink">
            <Avatar name={shift.assigneeName ?? '?'} size={18} />
            <span className="truncate">{shift.assigneeName}</span>
            {shift.notes && (
              <span title="Has a note" aria-label="Has a note">
                📝
              </span>
            )}
            {shift.published && shift.status === 'assigned' && shift.ackedAt && (
              <span className="text-forest" title="Acknowledged" aria-label="Acknowledged">
                ✓
              </span>
            )}
          </span>
        )}
        {showPosition && <span className="truncate text-xs text-stone">{shift.positionName}</span>}
      </span>
    </button>
  )
}
